import { useState } from 'react';
import { Kaart, KaartKop, Knop, Modaal, Veld, useToast } from './ui';
import { useAuth } from '../lib/auth';
import { START_RATING, chronologisch, replay } from '../lib/elo';
import { datumVeld, fmtDatumTijd, tijdVeld, toonNaam } from '../lib/format';
import { BEZWAARTERMIJN } from '../lib/demo';
import { rangSprong } from '../lib/prestaties';
import { useBevestigUitslag, useLeague, useWijzigUitslag } from '../lib/queries';
import type { Match, Player } from '../lib/types';

/* ==========================================================================
   Akkoord op een uitslag.

   Een ingevoerd potje telt pas als er van beide teams iemand tekent. Tot die
   tijd staat het hier: bij jou als de vraag bij jou ligt, en anders met de naam
   van degene op wie gewacht wordt.

   Ben je het niet eens met de stand? Dan pas je hem aan. Dat wist alle
   akkoorden — ook dat van de invoerder — en de vraag ligt weer bij de ander.
   Een uitslag terugdraaien bestaat met opzet niet: hij is gespeeld, alleen de
   cijfers klopten niet.
   ========================================================================== */

/** Wie moeten er nog tekenen: de teams zonder handtekening. */
export function wachtOp(potje: Match, zoekSpeler: (id: string) => Player): Player[] {
  const kantOpen = (team: string[]) => !team.some((id) => potje.akkoord.includes(id));
  const ids = [...(kantOpen(potje.teamA) ? potje.teamA : []), ...(kantOpen(potje.teamB) ? potje.teamB : [])];
  return ids.map(zoekSpeler);
}

/**
 * Hoeveel dagen heeft de andere kant nog? Na de bezwaartermijn telt een uitslag
 * vanzelf mee — zie migratie 017. Null zodra die dag geweest is.
 */
export function dagenTeGaan(potje: Match): number | null {
  const over = new Date(potje.createdAt).getTime() + BEZWAARTERMIJN - Date.now();
  return over > 0 ? Math.ceil(over / 86400000) : null;
}

/** Ligt de vraag bij mij? Alleen als ik meespeelde en nog niet tekende. */
export function ligtBijMij(potje: Match, ikId: string): boolean {
  return (
    potje.status === 'wacht' &&
    [...potje.teamA, ...potje.teamB].includes(ikId) &&
    !potje.akkoord.includes(ikId)
  );
}

export function UitslagModaal({
  potje,
  zoekSpeler,
  onSluit
}: {
  potje: Match;
  zoekSpeler: (id: string) => Player;
  onSluit: () => void;
}) {
  const wijzigen = useWijzigUitslag();
  const meld = useToast();
  const gespeeld = new Date(potje.playedAt);

  const [scoreA, setScoreA] = useState(String(potje.scoreA));
  const [scoreB, setScoreB] = useState(String(potje.scoreB));
  const [datum, setDatum] = useState(datumVeld(gespeeld));
  const [tijd, setTijd] = useState(tijdVeld(gespeeld));
  const [fout, setFout] = useState<string | null>(null);

  const naam = (ids: string[]) => ids.map((id) => toonNaam(zoekSpeler(id))).join(' & ');

  async function bewaar() {
    const a = Number(scoreA);
    const b = Number(scoreB);

    if (!scoreA.trim() || !scoreB.trim() || Number.isNaN(a) || Number.isNaN(b)) {
      setFout('Vul beide standen in.');
      return;
    }
    if (a === b) {
      setFout('Gelijkspel bestaat niet aan de tafel — er moet een winnaar zijn.');
      return;
    }
    if (a < 0 || b < 0 || a > 10 || b > 10) {
      setFout('Een stand ligt tussen 0 en 10.');
      return;
    }

    const moment = new Date(`${datum}T${tijd}`);
    if (Number.isNaN(moment.getTime())) {
      setFout('Datum of tijd klopt niet.');
      return;
    }
    if (moment.getTime() > Date.now() + 60000) {
      setFout('Het tijdstip van het potje ligt in de toekomst.');
      return;
    }

    try {
      await wijzigen.mutateAsync({ id: potje.id, scoreA: a, scoreB: b, playedAt: moment.toISOString() });
      meld('Stand aangepast. De andere kant moet hem opnieuw bevestigen.');
      onSluit();
    } catch (err) {
      setFout(err instanceof Error ? err.message : 'Aanpassen mislukte.');
    }
  }

  return (
    <Modaal
      titel="Stand aanpassen"
      onSluit={onSluit}
      voet={
        <>
          <Knop onClick={onSluit}>Annuleren</Knop>
          <Knop variant="primair" onClick={() => void bewaar()} disabled={wijzigen.isPending}>
            {wijzigen.isPending ? 'Bezig…' : 'Opslaan'}
          </Knop>
        </>
      }
    >
      {fout ? <div className="mb-3 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">{fout}</div> : null}

      <p className="mb-4 text-sm leading-relaxed text-krijt-600">
        Iedereen die deze uitslag al erkende, erkende een andere stand. Zodra je opslaat gaan alle akkoorden eraf en
        ligt de vraag weer bij de andere kant.
      </p>

      <div className="flex items-end gap-3">
        <Veld label={naam(potje.teamA)}>
          <input
            type="number"
            min={0}
            max={10}
            inputMode="numeric"
            className="veld nums w-24 py-2.5 text-center text-xl font-semibold"
            value={scoreA}
            onChange={(e) => setScoreA(e.target.value)}
          />
        </Veld>
        <span className="pb-6 text-krijt-400">–</span>
        <Veld label={naam(potje.teamB)}>
          <input
            type="number"
            min={0}
            max={10}
            inputMode="numeric"
            className="veld nums w-24 py-2.5 text-center text-xl font-semibold"
            value={scoreB}
            onChange={(e) => setScoreB(e.target.value)}
          />
        </Veld>
      </div>

      <Veld label="Gespeeld op" help="Alleen aanpassen als het tijdstip echt verkeerd staat; de historie wordt opnieuw doorgerekend.">
        <div className="flex items-center gap-2">
          <input type="date" className="veld w-auto" value={datum} onChange={(e) => setDatum(e.target.value)} />
          <input type="time" className="veld w-auto" value={tijd} onChange={(e) => setTijd(e.target.value)} />
        </div>
      </Veld>
    </Modaal>
  );
}

/**
 * De kaart met alles wat nog niet rond is. Boven staat wat op jou wacht, daar
 * onder wat op een ander wacht — die tweede groep is er om te kunnen porren,
 * niet om iets aan te doen.
 */
export function OpenstaandeUitslagen({ pouleId }: { pouleId: string | null }) {
  const { profile } = useAuth();
  const ikId = profile?.id ?? '';
  const { matches, wachtend, zoekSpeler } = useLeague(pouleId);
  const bevestigen = useBevestigUitslag();
  const meld = useToast();
  const [aanpassen, setAanpassen] = useState<Match | null>(null);

  const bijMij = wachtend.filter((m) => ligtBijMij(m, ikId));
  const bijEenAnder = wachtend.filter((m) => !ligtBijMij(m, ikId));


  if (!wachtend.length) return null;

  async function bevestig(potje: Match) {
    try {
      await bevestigen.mutateAsync(potje.id);

      /*
       * Pas nu telt het potje mee, dus pas nu kan er iemand promoveren. We
       * rekenen de hele historie mét dit potje na in plaats van de bijstelling
       * erbij op te tellen: bij een teruggedateerd potje schuift alles erna mee.
       */
      const na = replay([...matches, potje]).ratings;
      const voor = replay(matches).ratings;

      for (const id of [...potje.teamA, ...potje.teamB]) {
        const sprong = rangSprong(voor[id] ?? START_RATING, na[id] ?? START_RATING);
        if (!sprong) continue;
        meld(
          sprong.soort === 'promotie'
            ? `${toonNaam(zoekSpeler(id))} promoveert naar ${sprong.naar.label}.`
            : `${toonNaam(zoekSpeler(id))} zakt terug naar ${sprong.naar.label}.`,
          sprong.soort === 'degradatie'
        );
      }

      meld('Bevestigd; de stand is doorgerekend.');
    } catch (err) {
      meld(err instanceof Error ? err.message : 'Bevestigen mislukte.', true);
    }
  }

  const regel = (m: Match) => {
    const winnaarA = m.scoreA > m.scoreB;
    return (
      <div key={m.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-krijt-200 py-2.5 last:border-b-0">
        <span className="w-[108px] shrink-0 text-xs text-krijt-500">{fmtDatumTijd(m.playedAt)}</span>
        <span className={`min-w-0 truncate ${winnaarA ? 'font-semibold' : 'text-krijt-600'}`}>
          {m.teamA.map((id) => toonNaam(zoekSpeler(id))).join(' & ')}
        </span>
        <span className="nums shrink-0 rounded-md bg-krijt-100 px-2.5 py-0.5 text-base font-semibold">
          {m.scoreA} – {m.scoreB}
        </span>
        <span className={`min-w-0 truncate ${winnaarA ? 'text-krijt-600' : 'font-semibold'}`}>
          {m.teamB.map((id) => toonNaam(zoekSpeler(id))).join(' & ')}
        </span>

        <span className="ml-auto flex items-center gap-2">
          {ligtBijMij(m, ikId) ? (
            <>
              <Knop variant="primair" klein onClick={() => void bevestig(m)} disabled={bevestigen.isPending}>
                Klopt
              </Knop>
              <Knop klein onClick={() => setAanpassen(m)}>
                Klopt niet
              </Knop>
            </>
          ) : (
            <span className="text-right text-xs text-krijt-500">
              wacht op {wachtOp(m, zoekSpeler).map(toonNaam).join(' of ')}
              {dagenTeGaan(m) ? (
                <span className="block text-krijt-400">telt vanzelf over {dagenTeGaan(m)} dagen</span>
              ) : null}
            </span>
          )}
        </span>
      </div>
    );
  };

  return (
    <>
      <Kaart>
        <KaartKop
          titel={bijMij.length ? 'Klopt deze stand?' : 'Nog niet bevestigd'}
          hint={
            bijMij.length
              ? 'Een uitslag telt pas als beide kanten hem erkennen — of vanzelf, na vier dagen'
              : 'Deze potjes tellen nog niet mee voor de rating'
          }
        />
        {chronologisch(bijMij).reverse().map(regel)}
        {bijMij.length && bijEenAnder.length ? <div className="h-3" /> : null}
        {chronologisch(bijEenAnder).reverse().map(regel)}
      </Kaart>

      {aanpassen ? (
        <UitslagModaal potje={aanpassen} zoekSpeler={zoekSpeler} onSluit={() => setAanpassen(null)} />
      ) : null}
    </>
  );
}
